/**
 * API Service for Payment Mandates
 */

import { fetchWithAuth } from '@/utils/api';
import type {
  PaymentMandate,
  InitiateGoCardlessMandateRequest,
  InitiateGoCardlessMandateResponse,
  CompleteGoCardlessMandateRequest,
  InitiateStripeMandateRequest,
  InitiateStripeMandateResponse,
  CompleteStripeMandateRequest,
  CreateManualMandateRequest,
  GoCardlessConfig,
  ConfigureGoCardlessRequest,
  MandateStatus,
  MandateProvider,
  SyncFromGoCardlessOptions,
  SyncResult,
  CSVImportRow,
  ImportResult,
  AssociateRequest,
  UnassociatedMandate,
  GoCardlessMandateInfo,
  // Stripe migration types
  StripeSyncOptions,
  StripeSyncResult,
  StripeCSVImportRow,
  StripeImportResult,
  StripeAssociateRequest,
  UnassociatedStripeCustomer,
  StripeCustomerInfo,
} from '../types/mandate.types';

const BASE_URL = '/payments';

/**
 * Helper to parse API response and handle errors
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const json = await response.json();

  if (!response.ok) {
    throw new Error(json.error?.message || json.message || 'Une erreur est survenue');
  }

  return json.data as T;
}

/**
 * Payment Mandates API
 */
export const mandateApi = {
  /**
   * List all mandates for the company
   */
  list: async (options?: {
    status?: MandateStatus;
    provider?: MandateProvider;
  }): Promise<PaymentMandate[]> => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.provider) params.append('provider', options.provider);

    const queryString = params.toString();
    const url = `${BASE_URL}/mandates${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {

    });
    return parseResponse<PaymentMandate[]>(response);
  },

  /**
   * Get mandate by ID
   */
  getById: async (id: string): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/${id}`,
      {}
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Get mandates for a client
   */
  getByClient: async (clientId: string): Promise<PaymentMandate[]> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/client/${clientId}`,
      {}
    );
    return parseResponse<PaymentMandate[]>(response);
  },

  /**
   * Get active mandate for a client
   */
  getActiveByClient: async (clientId: string): Promise<PaymentMandate | null> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/client/${clientId}/active`,
      {}
    );
    return parseResponse<PaymentMandate | null>(response);
  },

  /**
   * Initiate GoCardless mandate (SEPA Direct Debit)
   */
  initiateGoCardless: async (
    data: InitiateGoCardlessMandateRequest
  ): Promise<InitiateGoCardlessMandateResponse> => {
    const response = await fetchWithAuth(`${BASE_URL}/mandates/gocardless/init`, {
      method: 'POST',

      body: JSON.stringify(data),
    });
    return parseResponse<InitiateGoCardlessMandateResponse>(response);
  },

  /**
   * Complete GoCardless mandate after customer authorization
   */
  completeGoCardless: async (
    data: CompleteGoCardlessMandateRequest
  ): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/gocardless/complete`,
      {
        method: 'POST',
  
        body: JSON.stringify(data),
      }
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Initiate Stripe mandate (Card/SEPA)
   */
  initiateStripe: async (
    data: InitiateStripeMandateRequest
  ): Promise<InitiateStripeMandateResponse> => {
    const response = await fetchWithAuth(`${BASE_URL}/mandates/stripe/init`, {
      method: 'POST',

      body: JSON.stringify(data),
    });
    return parseResponse<InitiateStripeMandateResponse>(response);
  },

  /**
   * Complete Stripe mandate after customer setup
   */
  completeStripe: async (data: CompleteStripeMandateRequest): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/stripe/complete`,
      {
        method: 'POST',
  
        body: JSON.stringify(data),
      }
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Create manual mandate
   */
  createManual: async (data: CreateManualMandateRequest): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/manual`,
      {
        method: 'POST',
  
        body: JSON.stringify(data),
      }
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Cancel mandate
   */
  cancel: async (id: string, reason?: string): Promise<void> => {
    const response = await fetchWithAuth(`${BASE_URL}/mandates/${id}`, {
      method: 'DELETE',

      body: reason ? JSON.stringify({ reason }) : undefined,
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(json.error?.message || 'Erreur lors de l\'annulation');
    }
  },
};

/**
 * GoCardless Configuration API
 */
export const goCardlessApi = {
  /**
   * Get GoCardless configuration
   */
  getConfig: async (): Promise<GoCardlessConfig | null> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/gocardless/config`,
      {}
    );
    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.error?.message || 'Erreur lors de la récupération de la config');
    }

    // Return null if no config, otherwise return the data
    return json.data ?? null;
  },

  /**
   * Configure GoCardless
   */
  configure: async (data: ConfigureGoCardlessRequest): Promise<void> => {
    const response = await fetchWithAuth(`${BASE_URL}/gocardless/configure`, {
      method: 'POST',

      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const json = await response.json();
      throw new Error(json.error?.message || 'Erreur lors de la configuration');
    }
  },

  /**
   * Disable GoCardless
   */
  disable: async (): Promise<void> => {
    const response = await fetchWithAuth(`${BASE_URL}/gocardless/config`, {
      method: 'DELETE',

    });

    if (!response.ok) {
      const json = await response.json();
      throw new Error(json.error?.message || 'Erreur lors de la désactivation');
    }
  },

  /**
   * Check connection status
   */
  getStatus: async (): Promise<{ connected: boolean; environment: string }> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/gocardless/status`,
      {}
    );
    return parseResponse<{ connected: boolean; environment: string }>(response);
  },
};

/**
 * Mandate Migration API
 * For migrating existing GoCardless mandates to the platform
 */
export const mandateMigrationApi = {
  /**
   * Sync mandates from GoCardless account
   */
  sync: async (options?: SyncFromGoCardlessOptions): Promise<SyncResult> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/migration/sync`,
      {
        method: 'POST',
  
        body: JSON.stringify(options || {}),
      }
    );
    return parseResponse<SyncResult>(response);
  },

  /**
   * Import mandates from CSV/JSON data
   */
  import: async (mandates: CSVImportRow[]): Promise<ImportResult> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/migration/import`,
      {
        method: 'POST',
  
        body: JSON.stringify({ mandates }),
      }
    );
    return parseResponse<ImportResult>(response);
  },

  /**
   * Manually associate a mandate to a client
   */
  associate: async (data: AssociateRequest): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/migration/associate`,
      {
        method: 'POST',
  
        body: JSON.stringify(data),
      }
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Get unassociated mandates pending association
   */
  getUnassociated: async (): Promise<UnassociatedMandate[]> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/migration/unassociated`,
      {}
    );
    return parseResponse<UnassociatedMandate[]>(response);
  },

  /**
   * Lookup a mandate by GoCardless ID
   */
  lookup: async (gocardlessMandateId: string): Promise<GoCardlessMandateInfo | null> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/mandates/migration/lookup/${gocardlessMandateId}`,
      {}
    );
    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.error?.message || 'Erreur lors de la recherche');
    }

    return json.data ?? null;
  },
};

/**
 * Stripe Migration API
 * For migrating existing Stripe customers to the platform
 */
export const stripeMigrationApi = {
  /**
   * Sync customers from Stripe account
   */
  sync: async (options?: StripeSyncOptions): Promise<StripeSyncResult> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/stripe/migration/sync`,
      {
        method: 'POST',
  
        body: JSON.stringify(options || {}),
      }
    );
    return parseResponse<StripeSyncResult>(response);
  },

  /**
   * Import customers from CSV/JSON data
   */
  import: async (customers: StripeCSVImportRow[]): Promise<StripeImportResult> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/stripe/migration/import`,
      {
        method: 'POST',
  
        body: JSON.stringify({ customers }),
      }
    );
    return parseResponse<StripeImportResult>(response);
  },

  /**
   * Manually associate a Stripe customer to a client
   */
  associate: async (data: StripeAssociateRequest): Promise<PaymentMandate> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/stripe/migration/associate`,
      {
        method: 'POST',
  
        body: JSON.stringify(data),
      }
    );
    return parseResponse<PaymentMandate>(response);
  },

  /**
   * Get unassociated Stripe customers pending association
   */
  getUnassociated: async (): Promise<UnassociatedStripeCustomer[]> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/stripe/migration/unassociated`,
      {}
    );
    return parseResponse<UnassociatedStripeCustomer[]>(response);
  },

  /**
   * Lookup a customer by Stripe ID
   */
  lookup: async (stripeCustomerId: string): Promise<StripeCustomerInfo | null> => {
    const response = await fetchWithAuth(
      `${BASE_URL}/stripe/migration/lookup/${stripeCustomerId}`,
      {}
    );
    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.error?.message || 'Erreur lors de la recherche');
    }

    return json.data ?? null;
  },
};
